import { useState, useEffect, useRef, useCallback } from 'react';
import { sessionService } from '../services/sessionService';

export const useSessionData = (unitId, selectedProject, selectedSession, isOpen) => {
  const [sessions, setSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [canData, setCanData] = useState(null);
  const [logData, setLogData] = useState(null);
  const [dbLogData, setDbLogData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const loadedChunksRef = useRef(new Map()); // Map: sessionId -> Set of chunk indices
  const pendingChunksRef = useRef(new Set());
  const currentSessionRef = useRef(null);
  const [selectedDbcFile, setSelectedDbcFile] = useState('');
  // Fetch sessions when frame opens
  useEffect(() => {
    if (isOpen && unitId) {
      setIsLoadingSessions(true);
      sessionService.fetchSessions(unitId)
        .then(data => {
          setSessions(data.sessions || []);
          setProjects(data.projects || []);
          setProjectInfo(data.projectInfo || null);
          setIsLoadingSessions(false);
        })
        .catch(err => {
          console.error('Failed to fetch sessions:', err);
          setIsLoadingSessions(false);
        });
    }
  }, [isOpen, unitId]);

  // Fetch sessions when project changes
  useEffect(() => {
    if (selectedProject && unitId) {
      setIsLoadingSessions(true);
      sessionService.fetchSessionsByProject(unitId, selectedProject)
        .then(data => {
          setSessions(data.sessions || []);
          setProjectInfo(data.projectInfo || null);
          setIsLoadingSessions(false);
        })
        .catch(err => {
          console.error('Failed to fetch sessions for project:', err);
          setIsLoadingSessions(false);
        });
    }
  }, [selectedProject, unitId]);

  // Fetch session data when session is selected
  useEffect(() => {
    if (selectedSession && unitId && selectedProject) {
      setLoading(true);
      currentSessionRef.current = selectedSession;
      if (!loadedChunksRef.current.has(selectedSession)) {
        loadedChunksRef.current.set(selectedSession, new Set());
      }
      pendingChunksRef.current = new Set();
      
      // Check if session has DB files from projectInfo
      const sessionFromProject = projectInfo?.sessions?.find(s => s.objectId === selectedSession);
      const hasDbFiles = sessionFromProject?.logFiles?.some(f => f.suffix === 'db');
      
      const requests = [
        sessionService.fetchGpsData(unitId, selectedProject, selectedSession)
      ];
      
      // Only fetch DB-based data if session has DB files
      if (hasDbFiles) {
        requests.unshift(sessionService.fetchCanSummary(unitId, selectedProject, selectedSession));
        requests.push(sessionService.fetchDbLogs(unitId, selectedProject, selectedSession));
      }
      
      Promise.all(requests)
      .then((results) => {
        let canDataResult, gpsDataResult, dbLogDataResult;
        
        if (hasDbFiles) {
          [canDataResult, gpsDataResult, dbLogDataResult] = results;
          setCanData(canDataResult);
          if (dbLogDataResult && !dbLogDataResult.error) {
            setDbLogData(dbLogDataResult);
          }
        } else {
          [gpsDataResult] = results;
          setCanData(null);
          setDbLogData(null);
        }
        
        // Build clgSources from sessionInfo
        const clgSources = sessionFromProject?.logFiles?.filter(f => f.suffix === 'clg').map(f => ({ filename: f.filename })) || [];
        setLogData({ gpsMessages: gpsDataResult?.gpsMessages || [], clgSources, logs: [] });
        setSessionInfo(sessionFromProject);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch session data:', err);
        setLoading(false);
      });
    } else {
      setCanData(null);
      setLogData(null);
      setDbLogData(null);
      setSessionInfo(null);
      setLoading(false);
      currentSessionRef.current = null;
      loadedChunksRef.current = new Map();
      pendingChunksRef.current = new Set();
    }
  }, [selectedSession, unitId, selectedProject, projectInfo]);

  // Periodic cleanup of old logs based on video time
  const lastVideoTimeRef = useRef(0);
  
  useEffect(() => {
    if (!selectedSession) return;
    
    const cleanupInterval = setInterval(() => {
      const currentVideoTime = lastVideoTimeRef.current;
      const chunkSize = 15;
      const currentChunkIndex = Math.floor(currentVideoTime / chunkSize);
      
      setLogData(prev => {
        if (!prev?.logs || prev.logs.length === 0) return prev;
        const beforeCount = prev.logs.length;
        const recentLogs = prev.logs.filter(log => {
          const logChunkIndex = Math.floor(log.timestamp / chunkSize);
          return Math.abs(logChunkIndex - currentChunkIndex) <= 3;
        });
        if (recentLogs.length !== beforeCount) {
          const sessionChunks = loadedChunksRef.current.get(selectedSession) || new Set();
          const remainingChunks = new Set(recentLogs.map(log => Math.floor(log.timestamp / chunkSize)));
          loadedChunksRef.current.set(selectedSession, remainingChunks);
        }
        return recentLogs.length !== beforeCount ? { ...prev, logs: recentLogs } : prev;
      });
    }, 5000);
    
    return () => clearInterval(cleanupInterval);
  }, [selectedSession]);

  const fetchLogChunk = useCallback((currentTime, dbcFile = null) => {
    if (!selectedSession || !unitId || !selectedProject || currentSessionRef.current !== selectedSession) return;
    
    lastVideoTimeRef.current = currentTime;
    
    const chunkSize = 15;
    const currentChunkIndex = Math.floor(currentTime / chunkSize);
    const nextChunkIndex = currentChunkIndex + 1;
    const sessionChunks = loadedChunksRef.current.get(selectedSession) || new Set();
    
    [currentChunkIndex, nextChunkIndex].forEach(chunkIndex => {
      const chunkStartTime = chunkIndex * chunkSize;
      const chunkEndTime = chunkStartTime + chunkSize;
      const logsInChunk = logData?.logs?.filter(log => 
        log.timestamp >= chunkStartTime && log.timestamp < chunkEndTime
      ) || [];
      
      if (pendingChunksRef.current.has(chunkIndex)) {
        // console.log(`[Chunk ${chunkIndex}] Skipped (pending fetch): ${logsInChunk.length} logs in memory for time ${chunkStartTime}-${chunkEndTime}s`);
        return;
      }
      
      if (sessionChunks.has(chunkIndex) && !dbcFile) {
        // If chunk is marked as loaded but has no logs in memory, refetch it
        if (logsInChunk.length === 0) {
          // console.log(`[Chunk ${chunkIndex}] Marked as loaded but no logs in memory, refetching time ${chunkStartTime}-${chunkEndTime}s`);
          sessionChunks.delete(chunkIndex); // Remove from loaded chunks so it can be refetched
        } else {
          //console.log(`[Chunk ${chunkIndex}] Skipped (already loaded): ${logsInChunk.length} logs in memory for time ${chunkStartTime}-${chunkEndTime}s`);
          return;
        }
      }
      
      const startTime = chunkIndex * chunkSize;
      const endTime = startTime + chunkSize;
      
      // console.log(`[Chunk ${chunkIndex}] Fetching logs for time ${startTime}-${endTime}s`);
      
      pendingChunksRef.current.add(chunkIndex);
      setLoadingChunks(true);
      
      sessionService.fetchClgChunk(unitId, selectedProject, selectedSession, startTime, endTime, dbcFile)
        .then(result => {
          // Only process if still on the same session
          if (currentSessionRef.current !== selectedSession) {
            pendingChunksRef.current.delete(chunkIndex);
            if (pendingChunksRef.current.size === 0) {
              setLoadingChunks(false);
            }
            return;
          }
          setSelectedDbcFile(result.selectedDbcFile);
          
          pendingChunksRef.current.delete(chunkIndex);
          
          if (pendingChunksRef.current.size === 0) {
            setLoadingChunks(false);
          }
          
          // Check if chunk has logs, mark as loaded even if empty
          sessionChunks.add(chunkIndex);
          loadedChunksRef.current.set(selectedSession, sessionChunks);
          
          // console.log(`[Chunk ${chunkIndex}] Received ${result.logs?.length || 0} logs from server`);
          
          if (result.logs && result.logs.length > 0) {
            setLogData(prev => {
              const allLogs = [...(prev?.logs || []), ...result.logs];
              const uniqueLogs = Array.from(new Map(allLogs.map(log => 
                [`${log.timestamp}-${log.canId}-${log.data}`, log]
              )).values()).sort((a, b) => a.timestamp - b.timestamp);
              // console.log(`[Chunk ${chunkIndex}] Total logs in memory after merge: ${uniqueLogs.length}`);
              return { ...prev, logs: uniqueLogs };
            });
          }
        })
        .catch(err => {
          // console.error(`[Chunk ${chunkIndex}] Failed to fetch:`, err);
          pendingChunksRef.current.delete(chunkIndex);
          if (pendingChunksRef.current.size === 0) {
            setLoadingChunks(false);
          }
        });
    });
  }, [selectedSession, unitId, selectedProject, logData?.logs]);

  const resetSessionData = () => {
    setCanData(null);
    setLogData(null);
    setDbLogData(null);
    setSessionInfo(null);
    currentSessionRef.current = null;
    loadedChunksRef.current = new Map();
  };

  return {
    sessions,
    projects,
    projectInfo,
    sessionInfo,
    isLoadingSessions,
    canData,
    logData,
    selectedDbcFile,
    dbLogData,
    loading,
    loadingChunks,
    resetSessionData,
    fetchLogChunk,
    loadedChunksRef
  };
};